import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge } from '../../../components/ui';
import { RefreshCw, Plus, Trash2, X, Edit, Package, ArrowRight } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { Link } from 'react-router-dom';
import { TIPOS_CON_RESTRINGIDOS_NUMERICOS, SET_TIPOS_CON_RESTRINGIDOS_NUMERICOS } from '../constants/restringidos';

// ══════════════════════════════════════════════════════════════════
// Types
// ══════════════════════════════════════════════════════════════════

interface SorteoOption {
  id: string;
  nombre: string;
  tipo_id: string;
}

interface LocalOption {
  id: string;
  nombre: string;
}

interface ConfigComercial {
  id: number;
  sorteo_nombre: string;
  local_id: string | null;
  commission_percentage: string | null;
  commission_exempt_amount: string | null;
  commission_max_amount: string | null;
  max_number_sales: number | null;
  limite_jugada?: string | null;
  alcance_limite_jugada?: string | null;
}

interface Restriccion {
  id: number;
  banca_id: string;
  local_id: string | null;
  local_nombre?: string | null;
  sorteo_nombre: string;
  numeros: string[];
  commission_percentage: string | null;
  commission_exempt_amount: string | null;
  commission_max_amount: string | null;
  max_number_sales: number | null;
  auto_dia: boolean;
  activo: boolean;
  created_at: string;
  updated_at: string | null;
}

interface BatchResultado {
  sorteo_id: string;
  sorteo_nombre: string;
  local_id: string | null;
  commission_percentage?: string | null;
  commission_exempt_amount?: string | null;
  commission_max_amount?: string | null;
  max_number_sales?: number | null;
}

interface MaximoApuestaConfig {
  monto: number | null;
  monto_banca: number | null;
  monto_local: number | null;
  origen: 'BANCA' | 'LOCAL' | 'SIN_LIMITE';
}

// ══════════════════════════════════════════════════════════════════
// RestringidosPage
// ══════════════════════════════════════════════════════════════════

export const RestringidosPage: React.FC = () => {
  // Data options
  const [sorteos, setSorteos] = useState<SorteoOption[]>([]);
  const [locales, setLocales] = useState<LocalOption[]>([]);

  // Form state
  const [selectedSorteo, setSelectedSorteo] = useState('');
  const [alcance, setAlcance] = useState<'global' | 'local'>('global');
  const [selectedLocal, setSelectedLocal] = useState('');
  const [numeros, setNumeros] = useState<string[]>([]);
  const [numeroInput, setNumeroInput] = useState('');
  const [sorteoComPct, setSorteoComPct] = useState('');
  const [limiteJugada, setLimiteJugada] = useState('');
  const [riesgoPct, setRiesgoPct] = useState('');
  const [commissionExempt, setCommissionExempt] = useState('');
  const [maxNumberSales, setMaxNumberSales] = useState('');
  const [autoDiaIndividual, setAutoDiaIndividual] = useState(false);
  const [maximoApuestaInput, setMaximoApuestaInput] = useState('');
  const [maximoApuestaActual, setMaximoApuestaActual] = useState<number | null>(null);
  const [guardandoMaximo, setGuardandoMaximo] = useState(false);

  // List state
  const [restricciones, setRestricciones] = useState<Restriccion[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [filtroVistaRestricciones, setFiltroVistaRestricciones] = useState<'todos' | 'global' | 'local'>('todos');

  // Batch state
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchTipo, setBatchTipo] = useState('NORMAL');
  const [batchSorteoIds, setBatchSorteoIds] = useState<string[]>([]);
  const [batchNumeros, setBatchNumeros] = useState<string[]>([]);
  const [batchNumeroInput, setBatchNumeroInput] = useState('');
  const [batchAutoDia, setBatchAutoDia] = useState(false);
  const [batchModo, setBatchModo] = useState<'append' | 'replace'>('append');
  const [batchAplicarA, setBatchAplicarA] = useState<'global' | 'locales'>('global');
  const [batchLocalIds, setBatchLocalIds] = useState<string[]>([]);
  const [batchSorteoComPct, setBatchSorteoComPct] = useState('');
  const [batchRiesgoPct, setBatchRiesgoPct] = useState('');
  const [batchLimiteJugada, setBatchLimiteJugada] = useState('');
  const [batchMaxNumberSales, setBatchMaxNumberSales] = useState('');
  const [batchCommissionExempt, setBatchCommissionExempt] = useState('');
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [batchSuccess, setBatchSuccess] = useState('');

  // Derived: tipo del sorteo seleccionado
  const sorteoSeleccionado = sorteos.find(s => s.nombre === selectedSorteo) || null;
  const tipoSeleccionado = sorteoSeleccionado?.tipo_id || '';
  const requiereNumeros = SET_TIPOS_CON_RESTRINGIDOS_NUMERICOS.has(tipoSeleccionado);
  const totalGlobal = restricciones.filter(r => !r.local_id).length;
  const totalLocal = restricciones.filter(r => Boolean(r.local_id)).length;
  const restriccionesFiltradas = restricciones.filter(r => {
    if (filtroVistaRestricciones === 'global') return !r.local_id;
    if (filtroVistaRestricciones === 'local') return Boolean(r.local_id);
    return true;
  });

  // Load sorteos and locales
  useEffect(() => {
    fetchAPI<SorteoOption[]>('/restringidos/sorteos')
      .then(data => {
        setSorteos(data);
        if (data.length > 0) setSelectedSorteo(data[0].nombre);
      })
      .catch(err => console.error('Error cargando sorteos:', err));

    fetchAPI<LocalOption[]>('/restringidos/locales')
      .then(setLocales)
      .catch(err => console.error('Error cargando locales:', err));
  }, []);

  // Load config comercial del sorteo cuando cambia sorteo/alcance/local
  const loadMaximoApuesta = useCallback(() => {
    fetchAPI<MaximoApuestaConfig>('/configuracion/maximo-apuesta')
      .then((data) => {
        setMaximoApuestaActual(data.monto_banca ?? data.monto ?? null);
        setMaximoApuestaInput(data.monto_banca == null ? '' : String(data.monto_banca));
      })
      .catch(() => {
        setMaximoApuestaActual(null);
        setMaximoApuestaInput('');
      });
  }, []);

  useEffect(() => {
    loadMaximoApuesta();
  }, [loadMaximoApuesta]);

  const guardarMaximoApuesta = async () => {
    setError('');
    setSuccess('');
    setGuardandoMaximo(true);
    try {
      const monto = maximoApuestaInput.trim() === '' ? null : Number(maximoApuestaInput);
      await fetchAPI<MaximoApuestaConfig>('/configuracion/maximo-apuesta', {
        method: 'PUT',
        body: JSON.stringify({ monto }),
      });
      await loadMaximoApuesta();
      setSuccess('Máximo por apuesta actualizado correctamente');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error guardando máximo por apuesta';
      setError(msg);
    } finally {
      setGuardandoMaximo(false);
    }
  };

  const loadConfigComercial = useCallback(() => {
    if (!selectedSorteo || !sorteoSeleccionado) return;
    let url = `/reglas-premio/config-comercial?sorteo_id=${encodeURIComponent(sorteoSeleccionado.id)}`;
    if (alcance === 'local' && selectedLocal) {
      url += `&local_id=${encodeURIComponent(selectedLocal)}`;
    }
    fetchAPI<ConfigComercial | null>(url)
      .then(config => {
        setSorteoComPct(config?.commission_percentage ?? '');
        setLimiteJugada(config?.limite_jugada ?? '');
      })
      .catch(() => {
        setSorteoComPct('');
        setLimiteJugada('');
      });
  }, [selectedSorteo, sorteoSeleccionado, alcance, selectedLocal]);

  useEffect(() => {
    loadConfigComercial();
  }, [loadConfigComercial]);

  // Load restricciones existentes del alcance seleccionado.
  // No se filtra por sorteo para visualizar todas las reglas aplicadas (incluyendo lotes).
  const loadRestricciones = useCallback(() => {
    setLoading(true);
    let url = '/restringidos';
    if (alcance === 'local' && selectedLocal) {
      url += `&local_id=${encodeURIComponent(selectedLocal)}`;
    }
    fetchAPI<Restriccion[]>(url)
      .then(setRestricciones)
      .catch(() => setRestricciones([]))
      .finally(() => setLoading(false));
  }, [alcance, selectedLocal]);

  useEffect(() => {
    loadRestricciones();
  }, [loadRestricciones]);

  const resetForm = () => {
    setNumeros([]);
    setNumeroInput('');
    setRiesgoPct('');
    setCommissionExempt('');
    setMaxNumberSales('');
    setAutoDiaIndividual(false);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  const addNumero = () => {
    const val = numeroInput.trim();
    if (!/^\d{2}$/.test(val)) {
      setError('Numero debe ser de 2 digitos (00-99)');
      return;
    }
    if (numeros.includes(val)) {
      setError(`Numero ${val} ya esta en la lista`);
      return;
    }
    setNumeros(prev => [...prev, val].sort());
    setNumeroInput('');
    setError('');
  };

  const removeNumero = (num: string) => {
    setNumeros(prev => prev.filter(n => n !== num));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNumero();
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!selectedSorteo) {
      setError('Seleccione un sorteo');
      return;
    }
    if (!limiteJugada) {
      alert('Debe ingresar límite por jugada');
      return;
    }
    if (requiereNumeros && numeros.length === 0 && !autoDiaIndividual) {
      setError('Agregue al menos un numero o active Día del mes automático');
      return;
    }

    setSaving(true);
    try {
      // Guardar comision del sorteo si cambió
      if (sorteoComPct || limiteJugada) {
        const comBody: Record<string, unknown> = {
          sorteo_id: sorteoSeleccionado?.id,
          sorteo_nombre: selectedSorteo,
          limite_jugada: Number(limiteJugada),
          alcance_limite_jugada: alcance === 'global' ? 'GLOBAL' : 'LOCAL',
        };
        if (sorteoComPct) {
          comBody.commission_percentage = parseFloat(sorteoComPct);
        }
        if (alcance === 'local' && selectedLocal) {
          comBody.local_id = selectedLocal;
        }
        await fetchAPI('/reglas-premio/config-comercial', {
          method: 'POST',
          body: JSON.stringify(comBody),
        });
      }

      // Guardar restriccion
      if (editingId) {
        const updateBody: Record<string, unknown> = {
          commission_max_amount: riesgoPct ? parseFloat(riesgoPct) : null,
          max_number_sales: maxNumberSales ? parseInt(maxNumberSales, 10) : null,
          auto_dia: requiereNumeros ? autoDiaIndividual : false,
        };
        if (requiereNumeros) {
          updateBody.numeros = numeros;
          updateBody.commission_exempt_amount = commissionExempt ? parseFloat(commissionExempt) : null;
        }
        await fetchAPI<Restriccion>(`/restringidos/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(updateBody),
        });
        setSuccess('Restriccion actualizada');
      } else {
        const body: Record<string, unknown> = {
          sorteo_id: sorteoSeleccionado?.id,
          sorteo_nombre: selectedSorteo,
          numeros: requiereNumeros ? numeros : [],
          auto_dia: requiereNumeros ? autoDiaIndividual : false,
        };
        if (alcance === 'local' && selectedLocal) {
          body.local_id = selectedLocal;
        }
        if (riesgoPct) body.commission_max_amount = parseFloat(riesgoPct);
        if (maxNumberSales) body.max_number_sales = parseInt(maxNumberSales, 10);
        if (requiereNumeros && commissionExempt) {
          body.commission_exempt_amount = parseFloat(commissionExempt);
        }

        await fetchAPI<Restriccion>('/restringidos', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        setSuccess('Restriccion guardada');
      }

      resetForm();
      loadRestricciones();
      loadConfigComercial();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error guardando';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r: Restriccion) => {
    setEditingId(r.id);
    setNumeros(r.numeros);
    setRiesgoPct(r.commission_max_amount ? r.commission_max_amount.toString() : '');
    setCommissionExempt(r.commission_exempt_amount ? r.commission_exempt_amount.toString() : '');
    setMaxNumberSales(r.max_number_sales != null ? r.max_number_sales.toString() : '');
    setAutoDiaIndividual(Boolean(r.auto_dia));
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminar esta restriccion?')) return;
    try {
      await fetchAPI(`/restringidos/${id}`, { method: 'DELETE' });
      loadRestricciones();
      if (editingId === id) resetForm();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error eliminando';
      setError(msg);
    }
  };

  // ── Batch helpers ─────────────────────────────────────────────
  const TIPOS_BATCH = TIPOS_CON_RESTRINGIDOS_NUMERICOS;

  // Sorteos filtrados por el tipo seleccionado en el formulario de lote
  const sorteosBatch = sorteos.filter(s => s.tipo_id === batchTipo);

  const addBatchNumero = () => {
    const val = batchNumeroInput.trim().padStart(2, '0');
    if (!/^\d{2}$/.test(val)) {
      setBatchError('Número debe ser de 2 dígitos (00-99)');
      return;
    }
    if (batchNumeros.includes(val)) {
      setBatchError(`Número ${val} ya está en la lista`);
      return;
    }
    setBatchNumeros(prev => [...prev, val].sort());
    setBatchNumeroInput('');
    setBatchError('');
  };

  const removeBatchNumero = (num: string) => {
    setBatchNumeros(prev => prev.filter(n => n !== num));
  };

  const toggleBatchSorteo = (id: string) => {
    setBatchSorteoIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const toggleBatchLocal = (id: string) => {
    setBatchLocalIds(prev =>
      prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]
    );
  };

  const handleBatchSave = async () => {
    setBatchError('');
    setBatchSuccess('');
    const requiereNumerosBatch = SET_TIPOS_CON_RESTRINGIDOS_NUMERICOS.has(batchTipo);

    if (batchSorteoIds.length === 0) {
      setBatchError('Seleccione al menos un sorteo');
      return;
    }
    if (batchAplicarA === 'locales' && batchLocalIds.length === 0) {
      setBatchError('Seleccione al menos un local o cambie a Global (Banca)');
      return;
    }

    const hayConfigComercial = Boolean(
      batchSorteoComPct ||
      batchRiesgoPct ||
      batchLimiteJugada ||
      batchMaxNumberSales ||
      batchCommissionExempt
    );

    if (batchNumeros.length === 0 && !batchAutoDia && !hayConfigComercial) {
      setBatchError('Agregue números, active "Día del mes" o configure campos comerciales');
      return;
    }

    if (!requiereNumerosBatch && (batchRiesgoPct || batchMaxNumberSales || batchCommissionExempt)) {
      setBatchError('Este tipo de sorteo no permite riesgo, monto máximo por número ni monto mínimo permitido');
      return;
    }

    setBatchSaving(true);
    try {
      const payload: Record<string, unknown> = {
        tipo: batchTipo,
        sorteo_ids: batchSorteoIds,
        numeros: batchNumeros,
        incluir_auto_dia: batchAutoDia,
        modo: batchModo,
      };
      if (batchAplicarA === 'locales') {
        payload.local_ids = batchLocalIds;
      }
      if (batchSorteoComPct) {
        payload.commission_percentage = parseFloat(batchSorteoComPct);
      }
      if (batchRiesgoPct) {
        payload.commission_max_amount = parseFloat(batchRiesgoPct);
      }
      if (batchLimiteJugada) {
        payload.limite_jugada = parseFloat(batchLimiteJugada);
        payload.alcance_limite_jugada = batchAplicarA === 'global' ? 'GLOBAL' : 'LOCAL';
      }
      if (batchMaxNumberSales) {
        payload.max_number_sales = parseInt(batchMaxNumberSales, 10);
      }
      if (batchCommissionExempt) {
        payload.commission_exempt_amount = parseFloat(batchCommissionExempt);
      }

      const res = await fetchAPI<{ ok: boolean; resultados: BatchResultado[]; total: number }>(
        '/restringidos/batch',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
      const alcanceTxt = batchAplicarA === 'global'
        ? 'global banca'
        : `${batchLocalIds.length} local(es)`;
      setBatchSuccess(`Lote guardado: ${res.total} registro(s) actualizados (${alcanceTxt})`);
      setBatchSorteoIds([]);
      setBatchNumeros([]);
      setBatchAutoDia(false);
      setBatchSorteoComPct('');
      setBatchRiesgoPct('');
      setBatchLimiteJugada('');
      setBatchMaxNumberSales('');
      setBatchCommissionExempt('');
      if (batchAplicarA === 'locales') {
        setBatchLocalIds([]);
      }
      loadRestricciones();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error guardando lote';
      setBatchError(msg);
    } finally {
      setBatchSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Banner PROMINENTE: Paquetes de Reglas es la fuente única */}
      <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
            <Package size={24} className="text-amber-700" />
          </div>
          <div className="flex-1">
            <p className="text-base font-bold text-amber-900">Esta página es solo de consulta</p>
            <p className="text-sm text-amber-700 mt-1">
              La configuración de reglas, comisiones y números restringidos ahora se administra desde <strong>Paquetes de Reglas</strong>.
              Los cambios realizados aquí no se guardarán.
            </p>
            <Link
              to="/banca/paquetes-reglas"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors"
            >
              Ir a Paquetes de Reglas
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Restringidos Comerciales</h1>
        <p className="text-gray-500">Reglas de riesgo y limites para sorteos y numeros especificos</p>
      </div>

      <Card>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Máximo por apuesta</h2>
            <p className="text-sm text-gray-500">Límite global por jugada para toda la banca. Déjelo vacío para sin límite.</p>
          </div>
          <div className="w-full sm:w-72">
            <label className="block text-sm font-medium text-gray-700 mb-1">Monto (₡)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={maximoApuestaInput}
              onChange={(e) => setMaximoApuestaInput(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Sin límite"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={guardarMaximoApuesta}
            disabled={guardandoMaximo}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-60"
          >
            {guardandoMaximo ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Valor vigente: {maximoApuestaActual == null ? 'Sin límite' : `₡${maximoApuestaActual.toLocaleString()}`}
        </p>
      </Card>

      {/* Formulario */}
      <Card>
        <div className="space-y-4">
          {/* Sorteo + Alcance */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sorteo</label>
              <select
                value={selectedSorteo}
                onChange={e => { setSelectedSorteo(e.target.value); resetForm(); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {sorteos.length === 0 && (
                  <option value="">Sin sorteos disponibles</option>
                )}
                {sorteos.map(s => (
                  <option key={s.nombre} value={s.nombre}>
                    {s.nombre} ({s.tipo_id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alcance</label>
              <div className="flex gap-2">
                <button
                  onClick={() => { setAlcance('global'); setSelectedLocal(''); resetForm(); }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    alcance === 'global'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Global (Banca)
                </button>
                <button
                  onClick={() => {
                    setAlcance('local');
                    if (locales.length > 0 && !selectedLocal) setSelectedLocal(locales[0].id);
                    resetForm();
                  }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    alcance === 'local'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Por Local
                </button>
              </div>
            </div>
          </div>

          {/* Local selector */}
          {alcance === 'local' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
              <select
                value={selectedLocal}
                onChange={e => { setSelectedLocal(e.target.value); resetForm(); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {locales.map(l => (
                  <option key={l.id} value={l.id}>{l.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Config comercial */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* % Comision del sorteo - PRIMERO */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                % Comision del sorteo
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={sorteoComPct}
                onChange={e => setSorteoComPct(e.target.value)}
                placeholder="Ej: 10.5"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Porcentaje de comision sobre ventas del sorteo</p>
            </div>

            {/* % Riesgo permitido: solo para NORMAL/REVENTADO/MEGA */}
            {requiereNumeros && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  % Riesgo permitido
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={riesgoPct}
                  onChange={e => setRiesgoPct(e.target.value)}
                  placeholder="Ej: 7"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">Monto permitido en numeros restringidos</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Límite por jugada
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={limiteJugada}
                onChange={e => setLimiteJugada(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="Ej: 5000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Tope acumulado por combinación según el alcance configurado
              </p>
            </div>

            {requiereNumeros && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto maximo por numero
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={maxNumberSales}
                  onChange={e => setMaxNumberSales(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="0 = sin limite"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Monto maximo que el vendedor puede vender por numero restringido. 0 = sin limite
                </p>
              </div>
            )}

            {/* Monto exento: solo para tipos con numeros */}
            {requiereNumeros && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto minimo permitido
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={commissionExempt}
                  onChange={e => setCommissionExempt(e.target.value)}
                  placeholder="0 = sin bloqueo"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">El vendedor puede vender hasta este monto sin bloqueo. 0 = sin bloqueo</p>
              </div>
            )}
          </div>

          {/* Numeros input - solo para NORMAL/REVENTADO/MEGA */}
          {requiereNumeros && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numeros restringidos
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={numeroInput}
                  onChange={e => setNumeroInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="00-99"
                  maxLength={2}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-center"
                />
                <button
                  onClick={addNumero}
                  className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
              {numeros.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {numeros.map(num => (
                    <span
                      key={num}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded-md text-sm font-mono"
                    >
                      {num}
                      <button
                        onClick={() => removeNumero(num)}
                        className="hover:text-danger-600"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {requiereNumeros && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoDiaIndividual}
                onChange={e => setAutoDiaIndividual(e.target.checked)}
                className="w-4 h-4 accent-primary-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Día del mes automático</span>
                <p className="text-xs text-gray-400">Agrega dinámicamente el número del día actual (ej: el día 7 → "07") como restringido</p>
              </div>
            </label>
          )}

          {/* Messages */}
          {error && (
            <div className="p-3 bg-danger-50 text-danger-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-success-50 text-success-700 rounded-lg text-sm">
              {success}
            </div>
          )}

          {/* Actions - DESHABILITADO: usar Paquetes de Reglas */}
          <div className="flex flex-wrap gap-3 items-center">
            <button
              disabled={true}
              className="px-5 py-3 bg-gray-300 text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed min-h-[44px]"
              title="Use Paquetes de Reglas para configurar restricciones"
            >
              Guardar (deshabilitado)
            </button>
            <span className="text-xs text-gray-500">Use Paquetes de Reglas</span>
            {editingId && (
              <button
                onClick={resetForm}
                className="px-5 py-3 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[44px]"
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Asignación por Lotes */}
      <Card>
        <button
          onClick={() => setBatchOpen(o => !o)}
          className="w-full flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold text-gray-900">Asignación por Lotes</h2>
          <span className="text-gray-400 text-sm">{batchOpen ? '▲ Ocultar' : '▼ Expandir'}</span>
        </button>

        {batchOpen && (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-gray-500">
              Asigna los mismos números restringidos a múltiples sorteos del mismo tipo en un solo paso.
            </p>

            {/* Tipo + Modo */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de sorteo</label>
                <select
                  value={batchTipo}
                  onChange={e => { setBatchTipo(e.target.value); setBatchSorteoIds([]); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {TIPOS_BATCH.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Modo</label>
                <select
                  value={batchModo}
                  onChange={e => setBatchModo(e.target.value as 'append' | 'replace')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="append">Agregar (mantener existentes)</option>
                  <option value="replace">Reemplazar (borrar y poner nuevos)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aplicar a</label>
                <select
                  value={batchAplicarA}
                  onChange={e => {
                    const nuevo = e.target.value as 'global' | 'locales';
                    setBatchAplicarA(nuevo);
                    if (nuevo === 'global') {
                      setBatchLocalIds([]);
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="global">Global (Banca)</option>
                  <option value="locales">Locales específicos</option>
                </select>
              </div>
            </div>

            {batchAplicarA === 'locales' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Locales ({locales.length} disponibles)
                </label>
                {locales.length === 0 ? (
                  <p className="text-sm text-gray-400">No hay locales disponibles en esta banca</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {locales.map(l => {
                      const isSelected = batchLocalIds.includes(l.id);
                      return (
                        <button
                          key={l.id}
                          onClick={() => toggleBatchLocal(l.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                            isSelected
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                          }`}
                        >
                          {l.nombre}
                        </button>
                      );
                    })}
                  </div>
                )}
                {batchLocalIds.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{batchLocalIds.length} local(es) seleccionado(s)</p>
                )}
              </div>
            )}

            {/* Selección de sorteos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sorteos ({sorteosBatch.length} disponibles de tipo {batchTipo})
              </label>
              {sorteosBatch.length === 0 ? (
                <p className="text-sm text-gray-400">No hay sorteos de tipo {batchTipo}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {sorteosBatch.map(s => {
                    const isSelected = batchSorteoIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        onClick={() => toggleBatchSorteo(s.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                          isSelected
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                        }`}
                      >
                        {s.nombre}
                      </button>
                    );
                  })}
                </div>
              )}
              {batchSorteoIds.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{batchSorteoIds.length} seleccionado(s)</p>
              )}
            </div>

            {/* Números */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Números a restringir</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={batchNumeroInput}
                  onChange={e => setBatchNumeroInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBatchNumero(); } }}
                  placeholder="00-99"
                  maxLength={2}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-center"
                />
                <button
                  onClick={addBatchNumero}
                  className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
              {batchNumeros.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {batchNumeros.map(num => (
                    <span
                      key={num}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded-md text-sm font-mono"
                    >
                      {num}
                      <button onClick={() => removeBatchNumero(num)} className="hover:text-danger-600">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Configuración comercial en lote */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">% Comisión del sorteo</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={batchSorteoComPct}
                  onChange={e => setBatchSorteoComPct(e.target.value)}
                  placeholder="Ej: 10.5"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {SET_TIPOS_CON_RESTRINGIDOS_NUMERICOS.has(batchTipo) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">% Riesgo permitido</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={batchRiesgoPct}
                    onChange={e => setBatchRiesgoPct(e.target.value)}
                    placeholder="Ej: 7"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Límite por jugada</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={batchLimiteJugada}
                  onChange={e => setBatchLimiteJugada(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Ej: 5000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {SET_TIPOS_CON_RESTRINGIDOS_NUMERICOS.has(batchTipo) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto máximo por número</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={batchMaxNumberSales}
                    onChange={e => setBatchMaxNumberSales(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="0 = sin límite"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              {SET_TIPOS_CON_RESTRINGIDOS_NUMERICOS.has(batchTipo) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto mínimo permitido</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={batchCommissionExempt}
                    onChange={e => setBatchCommissionExempt(e.target.value)}
                    placeholder="0 = sin bloqueo"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}
            </div>

            {/* Auto-día */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={batchAutoDia}
                onChange={e => setBatchAutoDia(e.target.checked)}
                className="w-4 h-4 accent-primary-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Día del mes automático</span>
                <p className="text-xs text-gray-400">Agrega dinámicamente el número del día actual (ej: el día 7 → "07") como restringido</p>
              </div>
            </label>

            {/* Messages */}
            {batchError && (
              <div className="p-3 bg-danger-50 text-danger-700 rounded-lg text-sm">{batchError}</div>
            )}
            {batchSuccess && (
              <div className="p-3 bg-success-50 text-success-700 rounded-lg text-sm">{batchSuccess}</div>
            )}

            <button
              disabled={true}
              className="px-5 py-3 bg-gray-300 text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed min-h-[44px]"
              title="Use Paquetes de Reglas para configurar restricciones en lote"
            >
              Aplicar lote (deshabilitado)
            </button>
          </div>
        )}
      </Card>

      {/* Lista de restricciones existentes */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Restricciones existentes
          </h2>
          <button
            onClick={loadRestricciones}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Refrescar"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => setFiltroVistaRestricciones('todos')}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              filtroVistaRestricciones === 'todos'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
            }`}
          >
            Todos ({restricciones.length})
          </button>
          <button
            type="button"
            onClick={() => setFiltroVistaRestricciones('global')}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              filtroVistaRestricciones === 'global'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
            }`}
          >
            Global ({totalGlobal})
          </button>
          <button
            type="button"
            onClick={() => setFiltroVistaRestricciones('local')}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              filtroVistaRestricciones === 'local'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
            }`}
          >
            Local ({totalLocal})
          </button>
        </div>

        {restriccionesFiltradas.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">
            {loading
              ? 'Cargando...'
              : filtroVistaRestricciones === 'global'
                ? 'No hay restricciones globales'
                : filtroVistaRestricciones === 'local'
                  ? 'No hay restricciones por local'
                  : alcance === 'local'
                    ? 'No hay restricciones para este local'
                    : 'No hay restricciones en la banca'}
          </p>
        ) : (
          <div className="space-y-3">
            {restriccionesFiltradas.map(r => (
              <div
                key={r.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900">{r.sorteo_nombre}</span>
                      <Badge variant={r.local_id ? 'primary' : 'neutral'}>
                        {r.local_id ? `Local: ${r.local_nombre || r.local_id}` : 'GLOBAL'}
                      </Badge>
                      {r.auto_dia && (
                        <Badge variant="warning">Día automático</Badge>
                      )}
                    </div>

                    {r.numeros.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {r.numeros.map(num => (
                          <span
                            key={num}
                            className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs font-mono"
                          >
                            {num}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-4 text-xs text-gray-500">
                      {r.commission_max_amount && (
                        <span>Riesgo: {r.commission_max_amount}%</span>
                      )}
                      {r.max_number_sales != null && r.max_number_sales > 0 && (
                        <span>Max por numero: ₡{r.max_number_sales.toLocaleString()}</span>
                      )}
                      {r.commission_exempt_amount && (
                        <span>Min permitido: ₡{parseFloat(r.commission_exempt_amount).toLocaleString()}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-1 ml-4">
                    <button
                      disabled={true}
                      className="p-1.5 text-gray-300 cursor-not-allowed rounded"
                      title="Edición deshabilitada - Use Paquetes de Reglas"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      disabled={true}
                      className="p-1.5 text-gray-300 cursor-not-allowed rounded"
                      title="Eliminación deshabilitada - Use Paquetes de Reglas"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default RestringidosPage;
